function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import * as api from "./sequences-api";
import { SceneThumb, voiceSeconds, snap, fmtLen } from "./sequence-common";

/*
 * The timeline card under the preview (mockup screen 3): one track of scenes whose widths follow their lengths,
 * transition markers between them, a "+" to add a scene, a Voice lane and a Music lane, and a playhead.
 *  - Click a scene to select it. Arrow keys move the selection; Alt + arrow keys move the scene itself.
 *  - Drag a scene onto another to reorder (HTML drag and drop, the storyboard's old behaviour).
 *  - Drag a scene's right edge to set its length: snaps to 0.1 s, never below the shortest-scene setting. While the
 *    drag runs the track holds its scale (pixels per second) so the edge follows the pointer; the length shows in a
 *    bubble. The edge is also a keyboard slider (arrows 0.1 s, Shift + arrows 1 s).
 */

const MAX_LEN = 60;
const TR_ICON = {
  cut: "cut",
  crossfade: "fade",
  slide: "slide"
};
export default class SequenceTimeline extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      dragFrom: null,
      dragOver: null,
      resize: null,
      addOpen: false
    });
    _defineProperty(this, "_cols", {});
    _defineProperty(this, "_blocks", {});
    _defineProperty(this, "_resizing", false);
    /* ---------- keyboard ---------- */
    _defineProperty(this, "onBlockKey", (e, i) => {
      const {
        segs,
        onSelect,
        onMove,
        editable
      } = this.props;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onSelect(segs[i]._key);
        return;
      }
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      e.preventDefault();
      const to = e.key === "ArrowLeft" ? i - 1 : i + 1;
      if (to < 0 || to >= segs.length) return;
      if (e.altKey || e.metaKey || e.ctrlKey) {
        if (!editable) return;
        const key = segs[i]._key;
        onMove(i, to);
        onSelect(key);
        this.focusBlock(key);
      } else {
        onSelect(segs[to]._key);
        this.focusBlock(segs[to]._key);
      }
    });
    _defineProperty(this, "onEdgeKey", (e, seg, len) => {
      if (!this.props.editable) return;
      const step = e.shiftKey ? 1 : 0.1;
      if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
        e.preventDefault();
        this.props.onLength(seg._key, this.clamp(len - step));
      } else if (e.key === "ArrowRight" || e.key === "ArrowUp") {
        e.preventDefault();
        this.props.onLength(seg._key, this.clamp(len + step));
      }
    });
    /* ---------- drag to reorder ---------- */
    _defineProperty(this, "dragStart", (e, i) => {
      if (this._resizing || !this.props.editable) {
        e.preventDefault();
        return;
      }
      this._from = i;
      this.setState({
        dragFrom: i
      });
      try {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", String(i));
      } catch (x) {/* no-op */}
    });
    _defineProperty(this, "dragOver", (e, i) => {
      if (this._from === null || this._from === undefined) return;
      e.preventDefault();
      if (this.state.dragOver !== i) this.setState({
        dragOver: i
      });
    });
    _defineProperty(this, "drop", (e, i) => {
      e.preventDefault();
      const from = this._from;
      this._from = null;
      this.setState({
        dragFrom: null,
        dragOver: null
      });
      if (from !== null && from !== undefined && from !== i) {
        const key = this.props.segs[from]._key;
        this.props.onMove(from, i);
        this.props.onSelect(key);
      }
    });
    _defineProperty(this, "dragEnd", () => {
      this._from = null;
      this.setState({
        dragFrom: null,
        dragOver: null
      });
    });
    /* ---------- drag the right edge to set the length ---------- */
    _defineProperty(this, "startResize", (e, seg, len) => {
      if (!this.props.editable || e.button > 0) return;
      e.preventDefault();
      e.stopPropagation();
      const col = this._cols[seg._key];
      const w = col ? col.getBoundingClientRect().width : 0;
      if (!w || !len) return;
      this._resizing = true;
      this.props.onSelect(seg._key);
      this.setState({
        resize: {
          key: seg._key,
          sec: len,
          s0: len,
          x0: e.clientX,
          pps: w / len
        }
      });
      window.addEventListener("pointermove", this.moveResize);
      window.addEventListener("pointerup", this.upResize);
      window.addEventListener("pointercancel", this.cancelResize);
    });
    _defineProperty(this, "moveResize", e => {
      const r = this.state.resize;
      if (!r) return;
      const sec = this.clamp(r.s0 + (e.clientX - r.x0) / r.pps);
      if (sec !== r.sec) this.setState({
        resize: {
          ...r,
          sec
        }
      });
    });
    _defineProperty(this, "upResize", () => {
      const r = this.state.resize;
      this.endResize();
      if (r && r.sec !== r.s0) this.props.onLength(r.key, r.sec);
    });
    _defineProperty(this, "cancelResize", () => this.endResize());
  }
  componentWillUnmount() {
    this.endResize(true);
  }
  focusBlock(key) {
    setTimeout(() => {
      const el = this._blocks[key];
      if (el && el.focus) el.focus();
    }, 30);
  }
  clamp(v) {
    const min = Math.max(0.5, Number(this.props.minScene) || 1);
    return Math.min(MAX_LEN, Math.max(min, snap(v)));
  }
  endResize(silent) {
    window.removeEventListener("pointermove", this.moveResize);
    window.removeEventListener("pointerup", this.upResize);
    window.removeEventListener("pointercancel", this.cancelResize);
    setTimeout(() => {
      this._resizing = false;
    }, 0);
    if (!silent) this.setState({
      resize: null
    });
  }
  render() {
    const {
      segs,
      lens,
      selKey,
      brand,
      playing,
      playT,
      total,
      onPlay,
      voiceOn,
      onVoiceOn,
      canVoice,
      musicUrl,
      editable,
      onSelect,
      onAdd,
      addDisabled,
      minScene
    } = this.props;
    const {
      resize,
      dragOver,
      dragFrom,
      addOpen
    } = this.state;
    const L = segs.map((s, i) => resize && resize.key === s._key ? resize.sec : lens[i]);
    const starts = [];
    L.reduce((t, l, i) => {
      starts[i] = t;
      return t + l;
    }, 0);
    const sum = L.reduce((t, l) => t + l, 0);
    // where the playhead sits: inside the playing scene while Play all runs, at the selected scene's start otherwise
    let phIdx = segs.findIndex(s => s._key === selKey);
    let phFrac = 0;
    if (playing || playT > 0) {
      phIdx = -1;
      for (let i = 0; i < segs.length; i++) {
        if (playT < starts[i] + L[i] || i === segs.length - 1) {
          phIdx = i;
          phFrac = Math.max(0, Math.min(1, (playT - starts[i]) / (L[i] || 1)));
          break;
        }
      }
    }
    const colStyle = i => resize ? {
      flex: "0 0 auto",
      width: `${Math.max(18, L[i] * resize.pps)}px`
    } : {
      flex: `${Math.max(0.5, L[i])} 1 0`
    };
    return /*#__PURE__*/React.createElement("div", {
      className: "sx-tl" + (resize ? " resizing" : "")
    }, /*#__PURE__*/React.createElement("div", {
      className: "sx-tlhead"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "sx-play",
      disabled: !segs.length,
      onClick: onPlay,
      "aria-label": playing ? "Pause the preview" : "Play all scenes"
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, playing ? "pause" : "play_arrow"), playing ? "Pause" : "Play all"), /*#__PURE__*/React.createElement("span", {
      className: "sx-time"
    }, /*#__PURE__*/React.createElement("b", null, api.fmtClock(playT)), " / ", api.fmtClock(total)), canVoice ? /*#__PURE__*/React.createElement("label", {
      className: "sx-voiceon",
      title: "Play each line in the chosen voice while the preview runs"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: !!voiceOn,
      onChange: e => onVoiceOn(e.target.checked)
    }), "With voice") : null, /*#__PURE__*/React.createElement("span", {
      className: "sp"
    }), /*#__PURE__*/React.createElement("span", {
      className: "sx-tlhint"
    }, editable ? "Drag a scene to move it · drag its right edge to change its length" : "Rendering: changes are saved when it finishes")), /*#__PURE__*/React.createElement("div", {
      className: "sx-tlbody"
    }, /*#__PURE__*/React.createElement("div", {
      className: "sx-gutter",
      "aria-hidden": "true"
    }, /*#__PURE__*/React.createElement("span", {
      className: "g1"
    }), /*#__PURE__*/React.createElement("span", null, "Voice"), /*#__PURE__*/React.createElement("span", null, "Music")), /*#__PURE__*/React.createElement("div", {
      className: "sx-track"
    }, /*#__PURE__*/React.createElement("div", {
      className: "sx-cols"
    }, segs.map((seg, i) => {
      const sel = seg._key === selKey;
      const vs = voiceSeconds(seg);
      const vFrac = L[i] ? Math.min(1, vs / L[i]) : 0;
      const over = !!seg.narration && vs > L[i] + 0.05;
      return /*#__PURE__*/React.createElement(React.Fragment, {
        key: seg._key
      }, i > 0 ? /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "sx-mk " + (segs[i].transitionIn || "crossfade"),
        title: `Into scene ${i + 1}: ${(api.TRANSITIONS.find(([k]) => k === segs[i].transitionIn) || ["", "Fade"])[1]}. Change it on scene ${i}.`,
        "aria-label": `Transition into scene ${i + 1}: ${TR_ICON[segs[i].transitionIn] || "fade"}`,
        onClick: () => onSelect(segs[i - 1]._key)
      }, /*#__PURE__*/React.createElement("i", null)) : null, /*#__PURE__*/React.createElement("div", {
        className: "sx-col" + (sel ? " sel" : "") + (dragOver === i && dragFrom !== i ? dragFrom > i ? " drop-before" : " drop-after" : "") + (dragFrom === i ? " dragging" : ""),
        style: colStyle(i),
        ref: el => {
          this._cols[seg._key] = el;
        },
        draggable: editable && !resize,
        onDragStart: e => this.dragStart(e, i),
        onDragOver: e => this.dragOver(e, i),
        onDrop: e => this.drop(e, i),
        onDragEnd: this.dragEnd
      }, /*#__PURE__*/React.createElement("div", {
        className: "sx-block",
        role: "button",
        tabIndex: 0,
        ref: el => {
          this._blocks[seg._key] = el;
        },
        "aria-pressed": sel,
        "aria-label": `Scene ${i + 1}, ${fmtLen(L[i])}${seg.overlayText ? `, ${seg.overlayText}` : ""}. Arrow keys pick a scene; Alt and arrow keys move it.`,
        title: `Scene ${i + 1} · ${fmtLen(L[i])}`,
        onClick: () => onSelect(seg._key),
        onKeyDown: e => this.onBlockKey(e, i)
      }, /*#__PURE__*/React.createElement(SceneThumb, {
        seg: seg,
        i: i,
        brand: brand
      }), /*#__PURE__*/React.createElement("span", {
        className: "n"
      }, i + 1), seg.warnings && seg.warnings.length ? /*#__PURE__*/React.createElement("span", {
        className: "warn material-icons",
        title: seg.warnings.join("\n")
      }, "error") : null), /*#__PURE__*/React.createElement("span", {
        className: "sx-edge" + (resize && resize.key === seg._key ? " on" : ""),
        role: "slider",
        tabIndex: editable ? 0 : -1,
        "aria-label": `Length of scene ${i + 1}`,
        "aria-valuemin": Math.max(0.5, Number(minScene) || 1),
        "aria-valuemax": MAX_LEN,
        "aria-valuenow": snap(L[i]),
        "aria-valuetext": fmtLen(L[i]),
        "aria-disabled": !editable,
        title: editable ? "Drag to change the length" : "",
        onPointerDown: e => this.startResize(e, seg, L[i]),
        onKeyDown: e => this.onEdgeKey(e, seg, L[i]),
        onDragStart: e => e.preventDefault()
      }), resize && resize.key === seg._key ? /*#__PURE__*/React.createElement("span", {
        className: "sx-lenbub"
      }, fmtLen(resize.sec)) : null, /*#__PURE__*/React.createElement("div", {
        className: "sx-vcell" + (over ? " over" : ""),
        title: seg.narration ? over ? "The voice runs longer than this scene" : `Voice · about ${fmtLen(vs)}` : "No voice on this scene"
      }, seg.narration ? /*#__PURE__*/React.createElement("i", {
        style: {
          width: `${Math.max(6, vFrac * 100)}%`
        }
      }) : null), /*#__PURE__*/React.createElement("div", {
        className: "sx-mspace"
      }), phIdx === i ? /*#__PURE__*/React.createElement("span", {
        className: "sx-ph",
        style: {
          left: `${phFrac * 100}%`
        },
        "aria-hidden": "true"
      }) : null));
    })), /*#__PURE__*/React.createElement("div", {
      className: "sx-music" + (musicUrl ? " on" : ""),
      style: resize ? {
        width: `${sum * resize.pps + Math.max(0, segs.length - 1) * 14}px`,
        right: "auto"
      } : undefined
    }, musicUrl ? /*#__PURE__*/React.createElement("span", null, "Music") : /*#__PURE__*/React.createElement("span", null, "No music \xB7 add it in Sound"))), /*#__PURE__*/React.createElement("div", {
      className: "sx-addcol"
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "sx-add",
      "aria-label": "Add a scene",
      "aria-expanded": addOpen,
      disabled: addDisabled,
      title: "Add a scene",
      onClick: () => this.setState({
        addOpen: !addOpen
      })
    }, "+"), addOpen ? /*#__PURE__*/React.createElement("span", {
      className: "sx-menu",
      onMouseLeave: () => this.setState({
        addOpen: false
      })
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      onClick: () => {
        this.setState({
          addOpen: false
        });
        onAdd("assets");
      }
    }, "From Assets"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      onClick: () => {
        this.setState({
          addOpen: false
        });
        onAdd("title");
      }
    }, "Title card at the start"), /*#__PURE__*/React.createElement("button", {
      type: "button",
      onClick: () => {
        this.setState({
          addOpen: false
        });
        onAdd("end");
      }
    }, "End card at the end")) : null)));
  }
}